import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { PalPage, PalTypography, PalIconButton } from "@palamar/fe-library";
import { withCardon } from "cardon";
import { useTranslation } from "react-i18next";
import { MdRefresh } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";

import styled from "@emotion/styled";
import {
  HourglassDisabled as HourglassDisabledIcon,
  Error as ErrorIcon,
  QuestionMark,
  Block,
} from "@mui/icons-material";
import { CircularProgress, Grid, Tab, Tabs, Switch, FormControlLabel } from "@mui/material";

import useLoading from "~common/hooks/useLoading";
import PRChart from "~components/Generic/PRChart";
import PRModal from "~components/Generic/PRModal";
import PRTable from "~components/Generic/PRTable";
import AggregateCard from "~components/Statistics/AggregateCard";
import { apiUrlStatisticGenerator } from "~constants";
import AlertHelper from "~helpers/AlertHelper";
import RenderHelper from "~helpers/RenderHelper";
import SanitizeHelper from "~helpers/SanitizeHelper";
import { callStatisticFigure, setHideBlanks } from "~store/figures/actions";
import { selectFigureFilters, selectHideBlanks } from "~store/figures/selectors";
import { downloadStatisticGeneratorReport } from "~store/statisticGenerator/actions";
import { getStatisticPage } from "~store/statisticPages/actions";
import { selectCurrentProject } from "~store/user/selectors";

import ReportFilter from "./ReportFilter";

const StyledModal = styled(PRModal)`
  .modal-body {
    max-height: 85vh;
    overflow: auto;
  }
`;

const StyledItem = styled(Grid)`
  justify-content: center;
  align-items: center;
  display: flex;
`;

const DynamicReportContent = forwardRef(function DynamicReportContent(
  { reportJson: reportJsonProp, reportId, isPage, reportJsonRef = null },
  ref
) {
  const currentProject = useSelector(selectCurrentProject);

  const dispatch = useDispatch();
  const [loading, q] = useLoading();

  const [reportJson, setReportJson] = useState([]);

  useEffect(() => {
    if (reportJsonRef) {
      reportJsonRef.current = reportJson;
    }
  }, [reportJson, reportJsonRef]);

  useEffect(() => {
    if (reportJsonProp?.length) {
      setReportJson(reportJsonProp);
    } else if (reportId) {
      q(
        dispatch(
          !isPage
            ? downloadStatisticGeneratorReport(currentProject.id, reportId)
            : getStatisticPage(currentProject.id, reportId)
        )
      )
        .then((response) => {
          if (isPage) {
            return { data: response?.page_data || [] };
          }
          return response;
        })
        .then((response) => {
          if (Array.isArray(response?.data)) {
            setReportJson(response?.data || []);
          } else if (typeof response?.data === "object") {
            setReportJson([response?.data]);
          } else if (response?.data) {
            AlertHelper.showWarning("Invalid report data format. Please inspect the report's low-code");
          }
        });
    }
  }, [dispatch, reportJsonProp, reportId, currentProject?.id, q, isPage]);

  return (
    <span ref={ref} className="markdown-wrapper">
      <Grid container spacing={2}>
        <JsonRenderer loading={loading} reportJson={reportJson} />
      </Grid>
    </span>
  );
});

function JsonRenderer({ reportJson, loading }) {
  const { previewId } = useParams();
  return (
    <>
      {reportJson.map((report, index) => {
        let Comp = null;
        if (report.type === "echarts") {
          Comp = <ReportChart key={index} loading={loading} report={report} />;
        } else if (report.type === "markdown") {
          const renderedMd = RenderHelper.renderMd(report.content, { html: true });
          let sanitizedHtml = SanitizeHelper.html(renderedMd);
          sanitizedHtml = RenderHelper.addBlankTargetToLinks(sanitizedHtml);

          Comp = (
            <div
              key={`element-${index}`}
              className="markdown-body"
              dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
              style={{ marginTop: 20, width: "100%", ...report.style }}
            />
          );
        } else if (report.type === "html") {
          let sanitizedHtml = SanitizeHelper.html(report.content);
          sanitizedHtml = RenderHelper.addBlankTargetToLinks(sanitizedHtml);
          Comp = (
            <div
              key={`element-${index}`}
              dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
              style={{ width: "100%", ...report.style }}
            />
          );
        } else if (report.type === "filter") {
          return <ReportFilter key={index} filter={report} style={{ marginTop: 20 }} />;
        } else if (report.type === "AggregateCard") {
          Comp = <AggregateCardComponent key={`element-${index}`} {...report} noKey />;
        } else if (report.type === "tabs") {
          Comp = (
            <TabComponent key={`element-${index}`} showHideBlank={report.showHideBlank} tabList={report.tabList} />
          );
        } else if (report.type === "table") {
          Comp = (
            <TableComponent
              key={`element-${index}`}
              columns={report.columns}
              data={report.data}
              figure_id={report.figure_id}
              sorts={report.ordering_fields}
              {...report}
            />
          );
        } else if (report.type === "figure") {
          Comp = (
            <FigureComponent
              key={`element-${index}`}
              {...report}
              columns={report?.columns || null}
              figure_id={report.figure_id}
              figure_type={report?.figure_type || null}
              filters={report?.filters || null}
              ordering_fields={report?.ordering_fields || null}
            />
          );
        }
        const defaultLayoutProps = { xs: 12 };
        const layoutProps = report.layoutProps || defaultLayoutProps;

        return (
          <Grid key={`grid-${previewId}-${index}`} item {...layoutProps}>
            {Comp}
          </Grid>
        );
      })}
    </>
  );
}
const StyledTab = styled(Tab)`
  font-weight: 600;
`;

function TabComponent({ tabList, showHideBlank = true }) {
  const [selectedTab, setSelectedTab] = useState(0);
  const tabNames = useMemo(() => tabList.map((tab) => tab.name), [tabList]);
  const tabContents = useMemo(() => tabList.map((tab) => tab.content), [tabList]);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const hideBlanks = useSelector(selectHideBlanks);
  const handleHideBlanks = (e) => {
    dispatch(setHideBlanks(e.target.checked));
  };
  return (
    <PalPage>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Grid container alignItems="center" justifyContent="space-between">
            <Tabs
              sx={(t) => ({
                "& .MuiTabs-flexContainer": {
                  flexWrap: "wrap",
                },
                ".MuiTab-root.Mui-selected": {
                  borderBottom: `2px solid ${t.palette.primary.main}`,
                  paddingBottom: "10px",
                },
              })}
              TabIndicatorProps={{ sx: { display: "none" } }}
              value={selectedTab}
              onChange={(_, newValue) => setSelectedTab(newValue)}
            >
              {tabNames.map((tabName, index) => (
                <StyledTab key={index} label={tabName} />
              ))}
            </Tabs>
            {showHideBlank && (
              <FormControlLabel
                control={<Switch checked={hideBlanks} onChange={handleHideBlanks} />}
                label={t("dashboard.reportChart.hideBlanks")}
                sx={{ ml: 2 }}
              />
            )}
          </Grid>
        </Grid>
        <Grid item xs={12}>
          {tabContents.map((tabContent, index) => (
            <div key={index} style={{ display: selectedTab === index ? "block" : "none", width: "100%" }}>
              <JsonRenderer reportJson={tabContent} />
            </div>
          ))}
        </Grid>
      </Grid>
    </PalPage>
  );
}

function FigureComponent({ figure_id, filters = {}, figure_type = null, columns = [], ordering_fields = [], ...rest }) {
  const dispatch = useDispatch();
  const currentProject = useSelector(selectCurrentProject);
  const [figureError, setFigureError] = useState("");
  const [isLoading, q] = useLoading();
  const [reportJson, setReportJson] = useState([]);
  const [initial, setInitial] = useState(false);
  const tableMode = useMemo(() => figure_type === "table", [figure_type]);
  const url = useMemo(() => {
    return apiUrlStatisticGenerator.callFigure.format(currentProject.id, figure_id);
  }, [currentProject.id, figure_id]);

  const filterSelector = useMemo(() => selectFigureFilters(filters), [filters]);

  const filterValues = useSelector(filterSelector);

  const fetchFigure = useCallback(
    async (abortSignal) => {
      try {
        const response = await q(
          dispatch(
            callStatisticFigure(currentProject.id, figure_id, filterValues, {
              noAlert: true,
              ...(abortSignal ? { signal: abortSignal } : {}),
              ...(initial ? { loading: true } : { loading: false }),
            })
          )
        );
        if (response?.status === "fail") {
          setFigureError(response.error);
          return;
        }
        if (response?.figure) {
          setReportJson([response.figure]);
        }
        setFigureError("");
      } catch (error) {
        if (!abortSignal?.aborted) {
          setFigureError("default");
          // AlertHelper.showError("An error occurred while loading figure data");
        }
      } finally {
        if (initial) {
          setInitial(false);
        }
      }
    },
    [dispatch, figure_id, currentProject?.id, filterValues, q, initial]
  );

  const errorReload = useCallback(() => {
    setFigureError("");
    fetchFigure();
  }, [fetchFigure]);

  useEffect(() => {
    setFigureError("");
    if (tableMode) {
      return;
    }
    const abortController = new AbortController();
    fetchFigure(abortController.signal);
    return () => {
      // Cleanup function to abort the fetch request if the component unmounts
      abortController.abort();
    };
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, figure_id, currentProject?.id, q, filterValues, figure_type, tableMode]);

  return (
    <PalPage>
      <Grid container spacing={2}>
        <Grid item sx={isLoading ? { textAlign: "center" } : {}} xs={12}>
          {isLoading ? (
            <CircularProgress size={80} />
          ) : figureError ? (
            <ErrorComponent error={figureError} onReload={errorReload} />
          ) : tableMode ? (
            <TableComponent
              {...rest}
              columns={columns}
              figure_id={figure_id}
              filters={filterValues}
              sorts={ordering_fields}
              url={url}
            />
          ) : (
            <JsonRenderer loading={isLoading} reportJson={reportJson} />
          )}
        </Grid>
      </Grid>
    </PalPage>
  );
}

function ErrorComponent({ error, onReload }) {
  const { t } = useTranslation();
  const renderError = (Icon, message) => (
    <StyledItem item direction="column" style={{ position: "relative" }} xs={12}>
      <PalIconButton style={{ position: "absolute", top: 0, right: 0 }} onClick={onReload}>
        <MdRefresh size={24} />
      </PalIconButton>
      <Icon color="error" fontSize="large" style={{ fontSize: 80 }} />
      <PalTypography color="error" sx={{ mt: 2 }}>
        {message}
      </PalTypography>
    </StyledItem>
  );

  switch (error) {
    case "Permission":
      return renderError(Block, t("dashboard.reportChart.noPermission"));
    case "Time Out":
      return renderError(HourglassDisabledIcon, t("dashboard.reportChart.timeOut"));
    case "Invalid figure id":
      return renderError(QuestionMark, t("dashboard.reportChart.invalidFigureId"));
    default:
      return renderError(ErrorIcon, t("dashboard.reportChart.unexpectedError"));
  }
}

function TableComponent({ data, columns, url, filters, sorts, figure_id, pageQueryString, ...rest }) {
  return (
    <PRTable
      inline
      columns={columns}
      data={data}
      pageQueryString={pageQueryString || "p" + figure_id}
      responseDataCountKey="figure.count"
      responseDataKey="figure.results"
      sorts={sorts}
      url={url}
      urlQuery={filters}
    />
  );
}

function AggregateCardComponent({ ...rest }) {
  return <AggregateCard {...rest} />;
}

function DynamicReportChartModalContent({ get, reportId }) {
  const printRef = useRef();

  const handlePrint = () => {
    const printContent = printRef.current;
    const WinPrint = window.open("", "", "left=0,top=0,width=800,height=900,toolbar=0,scrollbars=0,status=0");

    WinPrint.document.write(printContent.innerHTML);
    // Copy <link>
    document.querySelectorAll("link[rel='stylesheet']").forEach((link) => {
      const newLink = WinPrint.document.createElement("link");
      newLink.rel = "stylesheet";
      newLink.href = link.href;
      WinPrint.document.head.appendChild(newLink);
    });

    // Copy <style>
    document.querySelectorAll("style").forEach((style) => {
      const newStyle = WinPrint.document.createElement("style");
      newStyle.textContent = style.textContent;
      WinPrint.document.head.appendChild(newStyle);
    });

    const customStyle = WinPrint.document.createElement("style");
    customStyle.textContent = `
    body, html {
      overflow: auto !important;
      height: auto !important;
      display: block !important;
      position: static !important; 
     
    }
    .echarts-for-react{
      transform: scale(0.65) !important;
      transform-origin: 0 0 !important;
    }
    .markdown-body{
      padding-right: 10px;
    }
  `;
    WinPrint.document.head.appendChild(customStyle);

    const viewportMeta = WinPrint.document.createElement("meta");
    viewportMeta.name = "viewport";
    viewportMeta.content = "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=0";
    WinPrint.document.head.appendChild(viewportMeta);

    WinPrint.document.body.classList.add("markdown-wrapper");

    WinPrint.document.close();
    WinPrint.focus();
    WinPrint.print();
  };

  return (
    <StyledModal
      centered
      isOpen={true}
      size="xl"
      submitText="Print"
      title={"Report Preview"}
      onClick={handlePrint}
      onClose={get(false)}
    >
      <DynamicReportContent ref={printRef} reportId={reportId} />
    </StyledModal>
  );
}

const hideBlanksForSingleSeries = (singleSeries) => {
  if (singleSeries?.type === "sankey") {
    const { links, data } = singleSeries;
    const filteredLinks = links.filter((link) => link.value > 0);
    const filteredData = data.filter((d) => {
      return filteredLinks.some((link) => link.source === d.name || link.target === d.name);
    });
    return { ...singleSeries, links: filteredLinks, data: filteredData };
  } else if (singleSeries?.type === "pie") {
    const { data } = singleSeries;
    if (!data || data.length === 0) return singleSeries;
    const filteredData = data.filter((item) => item.value > 0);
    if (filteredData.length === 0) return null;
    return { ...singleSeries, data: filteredData };
  }
  return { ...singleSeries };
};

const hideBlanksForOption = (option) => {
  const { series, legend } = option;
  if (!series || series.length === 0) return option;
  const filteredSeries = series
    .map((singleSeries) => {
      const result = hideBlanksForSingleSeries(singleSeries);
      return result;
    })
    .filter(Boolean);

  // Clean the legends too
  if (!legend?.data || legend?.data.length === 0) return { ...option, series: filteredSeries, legend };
  const possibleLegends = filteredSeries.flatMap((singleSeries) => {
    if (singleSeries?.type === "sankey") {
      return [];
    }
    return singleSeries.data.map((item) => item.name);
  });
  const filteredLegends = legend.data.filter((legend) => possibleLegends.includes(legend.name));
  return {
    ...option,
    series: filteredSeries,
    legend: {
      ...legend,
      data: filteredLegends,
    },
  };
};

const ReportChart = ({ report, loading }) => {
  const { option, style, height, ...rest } = report;
  const hideBlanks = useSelector(selectHideBlanks);
  const newOption = useMemo(() => {
    return hideBlanks ? hideBlanksForOption(option) : option;
  }, [option, hideBlanks]);
  return (
    <PRChart
      notMerge
      height={height || "500px"}
      loading={loading}
      option={newOption}
      {...rest}
      renderer="svg"
      style={style}
    />
  );
};

const DynamicReportChartModal = withCardon(DynamicReportChartModalContent, { destroyOnHide: true });

export { DynamicReportContent, DynamicReportChartModal };
export default DynamicReportChartModal;
